/**
 * The password rules a tenant sets (Settings > Security), as the backend describes them on the
 * reads that precede choosing a password: the reset-token read, the invitation read and
 * `/auth/account/password-policy`. `rules` are the backend's own sentences, so the list a page
 * shows, the live check and a refusal all say the same thing.
 */
export interface PasswordPolicy {
  min_length: number
  require_uppercase: boolean
  require_lowercase: boolean
  require_digit: boolean
  require_symbol: boolean
  rules: string[]
}

/** What applies when no policy has been read yet: the platform's own minimum. */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  min_length: 8,
  require_uppercase: false,
  require_lowercase: false,
  require_digit: false,
  require_symbol: false,
  rules: ['At least 8 characters'],
}

const isSymbol = (c: string) => !/[\p{L}\p{N}\s]/u.test(c)

/**
 * Each rule of `policy` with whether `password` meets it, in the order the backend lists them.
 * Mirrors services/security_policy_service.password_violations so the page never passes a
 * password the server refuses, or refuses one the server would take.
 */
export function checkPassword(password: string, policy: PasswordPolicy): { rule: string; met: boolean }[] {
  const chars = Array.from(password)
  const checks: { rule: string; met: boolean }[] = [
    { rule: `At least ${policy.min_length} characters`, met: chars.length >= policy.min_length },
  ]
  if (policy.require_uppercase) {
    checks.push({ rule: 'At least one uppercase letter', met: chars.some((c) => /\p{Lu}/u.test(c)) })
  }
  if (policy.require_lowercase) {
    checks.push({ rule: 'At least one lowercase letter', met: chars.some((c) => /\p{Ll}/u.test(c)) })
  }
  if (policy.require_digit) {
    checks.push({ rule: 'At least one number', met: chars.some((c) => /\p{Nd}/u.test(c)) })
  }
  if (policy.require_symbol) {
    checks.push({ rule: 'At least one symbol (for example ! @ # $ %)', met: chars.some(isSymbol) })
  }
  return checks
}

/** The rules `password` misses, as sentences. Empty means the password may be submitted. */
export function unmetRules(password: string, policy: PasswordPolicy): string[] {
  return checkPassword(password, policy).filter((c) => !c.met).map((c) => c.rule)
}

/** One sentence for a form error, matching the backend's refusal. */
export function passwordRefusal(unmet: string[]): string {
  const lowered = unmet.map((r) => r.charAt(0).toLowerCase() + r.slice(1))
  return `Password does not meet your organization's requirements: ${lowered.join('; ')}.`
}
